import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Modal dialog: role="dialog", labelled by its title, focus moves in and returns on close, Tab stays inside,
// Escape closes (unless `busy`). The backdrop does not close it, so a half-filled form is not lost by a stray click.
const Dialog = ({ title, onClose, children, footer, wide = false, busy = false }) => {
  const titleId = useId();
  const ref = useRef(null);
  const busyRef = useRef(busy);
  const closeRef = useRef(onClose);

  useEffect(() => {
    busyRef.current = busy;
    closeRef.current = onClose;
  });

  useEffect(() => {
    const node = ref.current;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const target = node.querySelector('.shp-dialog__body')?.querySelector(FOCUSABLE) || node.querySelector(FOCUSABLE);
    target?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous instanceof HTMLElement && document.contains(previous)) previous.focus();
    };
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busyRef.current) closeRef.current?.();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = [...ref.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return createPortal(
    <div className="shp-overlay" onKeyDown={onKeyDown}>
      <div ref={ref} className={`shp-dialog ${wide ? 'shp-dialog--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="shp-dialog__head">
          <h2 id={titleId} className="shp-dialog__title">{title}</h2>
          <button type="button" className="shp-iconbtn" onClick={onClose} disabled={busy} aria-label="Zavřít okno"><X size={20} aria-hidden="true" /></button>
        </div>
        <div className="shp-dialog__body">{children}</div>
        {footer && <div className="shp-dialog__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Dialog;
